import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { observer } from 'mobx-react';
import {
    FlexDiv,
    Block,
    Heading4,
    TextDefault,
    ThemedButton,
    AssessmentStepIndicator,
} from './ui';
import { Card, CardBody } from 'reactstrap';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { Menu, MenuItem, MenuList, IconButton, Fab, Typography, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Button } from '@mui/material';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import {
    RuntimePresentationView,
    RuntimeInteractiveBlockView,
} from './RuntimeScenarioViews';

const INTERACTIVE_BLOCK_TYPES = [
    'radio',
    'checkbox',
    'numericalscale',
    'yes-no-list',
    'orderablelist',
    'conversation',
    'password-tester',
    'decision',
    'groupOfBlocks',
    'blockHidden',
    'questionwithbuttons',
    'questionwithimage',
    'questionwithsocialpost',
];

const BLOCK_LABELS = {
    radio: 'Radio buttons',
    checkbox: 'Checkboxes',
    numericalscale: 'Numerical scale',
    'yes-no-list': 'Yes / No list',
    orderablelist: 'Orderable list',
    conversation: 'Conversation',
    'password-tester': 'Password tester',
    decision: 'Decision tree',
    groupOfBlocks: 'Group of blocks',
    blockHidden: 'Hidden block',
    questionwithbuttons: 'Question with buttons',
    questionwithimage: 'Question with image',
    questionwithsocialpost: 'Social post question',
};

const PRESENTATION_TYPES = [
    'illustration',
    'interactive-img',
    'email',
    'message',
    'password',
    'question',
    'video',
    'custom',
];

const PresentationArea = ({
    presentation,
    presentationTypes,
    presentationSelected,
    onOpenPresentationProperties,
    onPickPresentationType,
}) => {
    const [localAnchor, setLocalAnchor] = useState(null);

    const typeLabel = (type) =>
        presentationTypes?.[type]?.name || type;

    if (!presentation) {
        return (
            <div className="presentation-area empty">
                <Card>
                    <CardBody>
                        <FlexDiv column alignCenter gap={16} padding={32}>
                            <TextDefault lighter>
                                No presentation — use + to add
                            </TextDefault>
                            <IconButton
                                color="primary"
                                size="large"
                                aria-label="Add presentation"
                                onClick={(e) => setLocalAnchor(e.currentTarget)}
                            >
                                <AddIcon fontSize="large" />
                            </IconButton>
                            <Menu
                                anchorEl={localAnchor}
                                open={Boolean(localAnchor)}
                                onClose={() => setLocalAnchor(null)}
                            >
                                <MenuList autoFocusItem>
                                    {PRESENTATION_TYPES.map((type) => (
                                        <MenuItem
                                            key={type}
                                            onClick={() => {
                                                onPickPresentationType(type);
                                                setLocalAnchor(null);
                                            }}
                                        >
                                            {typeLabel(type)}
                                        </MenuItem>
                                    ))}
                                </MenuList>
                            </Menu>
                        </FlexDiv>
                    </CardBody>
                </Card>
            </div>
        );
    }

    return (
        <div
            className={`presentation-area ${presentationSelected ? 'selected' : ''}`}
        >
            <div className="scenario-surface scenario-surface--presentation">
                <div className="scenario-surface__chrome">
                    <Typography variant="caption" color="text.secondary">
                        Presentation · {presentation.type}
                    </Typography>
                    <span>
                        <IconButton
                            size="small"
                            aria-label="Edit presentation properties"
                            onClick={() => onOpenPresentationProperties()}
                        >
                            <EditIcon fontSize="small" />
                        </IconButton>
                    </span>
                </div>
                <div className="scenario-surface__body">
                    <RuntimePresentationView presentation={presentation} />
                </div>
            </div>
        </div>
    );
};

function QuestionCanvas({
    question,
    presentationTypes,
    selectedBlockId,
    presentationSelected,
    onSelectBlockId,
    onSelectPresentation,
    onRemoveBlock,
    onReorderBlock,
    onAddBlock,
    onSetPresentation,
    /** Step indicator colour (e.g. LMS branding). Optional. */
    primaryColor,
}) {

    const blocks = useMemo(
        () => question.ui.blocks || [],
        [question.ui.blocks]
    );
    const [carouselIndex, setCarouselIndex] = useState(0);
    const [blockMenuAnchor, setBlockMenuAnchor] = useState(null);
    const [deleteDialogBlockId, setDeleteDialogBlockId] = useState(null);

    useEffect(() => {
        const n = blocks.length;
        if (n === 0) {
            setCarouselIndex(0);
            return;
        }
        setCarouselIndex((i) => Math.min(Math.max(i, 0), n - 1));
    }, [blocks.length]);

    useEffect(() => {
        if (!selectedBlockId || blocks.length === 0) return;
        const idx = blocks.findIndex((b) => b.id === selectedBlockId);
        if (idx >= 0) setCarouselIndex(idx);
    }, [selectedBlockId, blocks]);

    const visibleBlock = blocks[carouselIndex] ?? null;

    const completedForIndicator = useMemo(
        () => blocks.map(() => false),
        [blocks]
    );

    const handleCarouselPrev = useCallback(() => {
        setCarouselIndex((i) => Math.max(0, i - 1));
    }, []);

    const handleCarouselNext = useCallback(() => {
        setCarouselIndex((i) => Math.min(blocks.length - 1, i + 1));
    }, [blocks.length]);

    const handleAddBlockClick = useCallback((e) => {
        setBlockMenuAnchor(e.currentTarget);
    }, []);

    const handlePickBlockType = useCallback(
        (type) => {
            onAddBlock(type);
            setBlockMenuAnchor(null);
        },
        [onAddBlock]
    );

    const handlePickPresentationType = useCallback(
        (type) => {
            onSetPresentation(type);
        },
        [onSetPresentation]
    );

    const confirmRemoveBlock = useCallback(() => {
        if (deleteDialogBlockId != null) {
            onRemoveBlock(deleteDialogBlockId);
        }
        setDeleteDialogBlockId(null);
    }, [deleteDialogBlockId, onRemoveBlock]);

    const handleReorderVisibleBlock = useCallback(
        (delta) => {
            if (!visibleBlock || !onReorderBlock) return;
            const idx = blocks.findIndex((b) => b.id === visibleBlock.id);
            if (idx < 0) return;
            const j = idx + delta;
            if (j < 0 || j >= blocks.length) return;
            onReorderBlock(visibleBlock.id, delta);
            setCarouselIndex(idx + delta);
        },
        [visibleBlock, blocks, onReorderBlock]
    );

    return (
        <div className="question-canvas">
            <Block padding={20} gap={20}>
                <Heading4>Scenario layout</Heading4>
                <TextDefault lighter>
                    Left: presentation. Right: one interactive block at a time
                    (same navigation as the learner view). Use the pencil icons to
                    open properties; change presentation type in the properties
                    panel.
                </TextDefault>

                <div className="canvas-section canvas-section--lr">
                    <div className="canvas-column canvas-column--presentation">
                        <TextDefault bold>Presentation</TextDefault>
                        <PresentationArea
                            presentation={question.ui.presentation}
                            presentationTypes={presentationTypes}
                            presentationSelected={presentationSelected}
                            onOpenPresentationProperties={onSelectPresentation}
                            onPickPresentationType={handlePickPresentationType}
                        />
                    </div>

                    <div className="canvas-column canvas-column--blocks">
                        <FlexDiv alignCenter gap={12} wrap>
                            <TextDefault bold>Interactive</TextDefault>
                            <ThemedButton
                                size="small"
                                startIcon={<AddIcon />}
                                onClick={handleAddBlockClick}
                                text="Add block"
                            />
                            <Menu
                                anchorEl={blockMenuAnchor}
                                open={Boolean(blockMenuAnchor)}
                                onClose={() => setBlockMenuAnchor(null)}
                            >
                                <MenuList autoFocusItem>
                                    {INTERACTIVE_BLOCK_TYPES.map((type) => (
                                        <MenuItem
                                            key={type}
                                            onClick={() =>
                                                handlePickBlockType(type)
                                            }
                                        >
                                            {BLOCK_LABELS[type] || type}
                                        </MenuItem>
                                    ))}
                                </MenuList>
                            </Menu>
                        </FlexDiv>

                        {blocks.length === 0 ? (
                            <Card>
                                <CardBody>
                                    <FlexDiv
                                        column
                                        alignCenter
                                        gap={16}
                                        padding={24}
                                    >
                                        <TextDefault lighter>
                                            No blocks yet
                                        </TextDefault>
                                        <ThemedButton
                                            startIcon={<AddIcon />}
                                            onClick={handleAddBlockClick}
                                            text="Add block"
                                        />
                                    </FlexDiv>
                                </CardBody>
                            </Card>
                        ) : (
                            <div
                                className={`interactive-stage ${selectedBlockId === visibleBlock?.id && !presentationSelected ? 'selected' : ''}`}
                            >
                                <div className="scenario-surface scenario-surface--interactive">
                                    <div className="scenario-surface__chrome">
                                        <Typography
                                            variant="caption"
                                            color="text.secondary"
                                        >
                                            Block {carouselIndex + 1} of{' '}
                                            {blocks.length} ·{' '}
                                            {BLOCK_LABELS[visibleBlock?.type] ||
                                                visibleBlock?.type}
                                        </Typography>
                                        <span className="scenario-surface__chrome-actions">
                                            {onReorderBlock && blocks.length > 1 ? (
                                                <>
                                                    <Button
                                                        size="small"
                                                        variant="text"
                                                        startIcon={
                                                            <ChevronLeftIcon fontSize="small" />
                                                        }
                                                        aria-label="Move block earlier in scenario"
                                                        disabled={carouselIndex === 0}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleReorderVisibleBlock(
                                                                -1
                                                            );
                                                        }}
                                                        sx={{
                                                            textTransform: 'none',
                                                            minWidth: 0,
                                                            px: 0.5,
                                                        }}
                                                    >
                                                        Move left
                                                    </Button>
                                                    <Button
                                                        size="small"
                                                        variant="text"
                                                        endIcon={
                                                            <ChevronRightIcon fontSize="small" />
                                                        }
                                                        aria-label="Move block later in scenario"
                                                        disabled={
                                                            carouselIndex >=
                                                            blocks.length - 1
                                                        }
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleReorderVisibleBlock(
                                                                1
                                                            );
                                                        }}
                                                        sx={{
                                                            textTransform: 'none',
                                                            minWidth: 0,
                                                            px: 0.5,
                                                        }}
                                                    >
                                                        Move right
                                                    </Button>
                                                </>
                                            ) : null}
                                            <IconButton
                                                size="small"
                                                aria-label="Edit block properties"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    if (visibleBlock) {
                                                        onSelectBlockId(
                                                            visibleBlock.id
                                                        );
                                                    }
                                                }}
                                            >
                                                <EditIcon fontSize="small" />
                                            </IconButton>
                                            <IconButton
                                                size="small"
                                                aria-label="Remove block"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    if (visibleBlock) {
                                                        setDeleteDialogBlockId(
                                                            visibleBlock.id
                                                        );
                                                    }
                                                }}
                                            >
                                                <DeleteIcon fontSize="small" />
                                            </IconButton>
                                        </span>
                                    </div>

                                    {blocks.length > 1 ? (
                                        <div className="interactive-stage__carousel">
                                            <div className="interactive-stage__progress">
                                                {blocks.map((_, i) => (
                                                    <AssessmentStepIndicator
                                                        key={`step-${blocks[i]?.id ?? i}`}
                                                        isCurrent={
                                                            carouselIndex === i
                                                        }
                                                        isActive={
                                                            i <
                                                                carouselIndex ||
                                                            completedForIndicator[i]
                                                        }
                                                        primaryColor={
                                                            primaryColor
                                                        }
                                                    />
                                                ))}
                                            </div>
                                            <div className="interactive-stage__nav">
                                                <Fab
                                                    size="small"
                                                    disabled={carouselIndex === 0}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleCarouselPrev();
                                                    }}
                                                    aria-label="Previous block"
                                                >
                                                    <NavigateBeforeIcon />
                                                </Fab>
                                                <Fab
                                                    size="small"
                                                    disabled={
                                                        carouselIndex >=
                                                        blocks.length - 1
                                                    }
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleCarouselNext();
                                                    }}
                                                    aria-label="Next block"
                                                >
                                                    <NavigateNextIcon />
                                                </Fab>
                                            </div>
                                        </div>
                                    ) : null}

                                    <div className="scenario-surface__body scenario-surface__body--interactive">
                                        <RuntimeInteractiveBlockView
                                            block={visibleBlock}
                                        />
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </Block>

            <Dialog
                open={deleteDialogBlockId != null}
                onClose={() => setDeleteDialogBlockId(null)}
            >
                <DialogTitle>Remove block?</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        This removes the block from the scenario. You can undo
                        only by reloading if you have not saved yet.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteDialogBlockId(null)}>
                        Cancel
                    </Button>
                    <Button
                        onClick={confirmRemoveBlock}
                        color="error"
                        variant="contained"
                    >
                        Remove
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    );
}

export default observer(QuestionCanvas);
